/**
 * PKCE utilities for Autodesk OAuth 2.0 Authorization Code + PKCE (S256).
 * Uses Web Crypto API only; no client secret.
 */

const BYTES_VERIFIER = 32;
const BYTES_STATE = 16;

/** Global object (window or global) for crypto access without using globalThis. */
function getGlobal() {
  if (typeof window !== "undefined") return window;
  if (typeof global !== "undefined") return global;
  return null;
}

/**
 * Base64url-encode a buffer (no padding, URL-safe).
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
function base64UrlEncode(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Generate a cryptographically random code_verifier (43–128 chars per RFC 7636).
 * @returns {string} Base64url-encoded random string.
 */
export function generateCodeVerifier() {
  const array = new Uint8Array(BYTES_VERIFIER);
  const g = getGlobal();
  if (g && g.crypto && g.crypto.getRandomValues) {
    g.crypto.getRandomValues(array);
  } else {
    throw new Error("PKCE requires crypto.getRandomValues");
  }
  return base64UrlEncode(array.buffer);
}

/**
 * Generate code_challenge from code_verifier using SHA-256 + base64url (S256).
 * @param {string} verifier - The code_verifier string.
 * @returns {Promise<string>} The code_challenge.
 */
export async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(digest);
}

/**
 * Generate a cryptographically random state parameter for CSRF protection.
 * @returns {string} Base64url-encoded random string.
 */
export function generateState() {
  const array = new Uint8Array(BYTES_STATE);
  const g = getGlobal();
  if (g && g.crypto && g.crypto.getRandomValues) {
    g.crypto.getRandomValues(array);
  } else {
    throw new Error("PKCE requires crypto.getRandomValues");
  }
  return base64UrlEncode(array.buffer);
}
